/**
 * Delete Account Section
 *
 * Danger-zone component for permanent account deletion.
 * Shows a "Delete Account" button that opens a confirmation dialog
 * requiring the user to type "DELETE" before proceeding.
 * On success, clears session and redirects to the home page.
 */

import { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useDeleteAccount } from "../../hooks/useCurrentUser";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";

export function DeleteAccountSection() {
  const { logout } = useAuth();
  const deleteAccount = useDeleteAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isConfirmed = confirmation === "DELETE";

  const handleDelete = async () => {
    if (!isConfirmed) return;
    setError(null);

    try {
      await deleteAccount.mutateAsync("DELETE");
      // Account deleted — log out and redirect
      logout();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to delete account.";
      setError(message);
    }
  };

  const handleClose = () => {
    if (deleteAccount.isPending) return;
    setIsOpen(false);
    setConfirmation("");
    setError(null);
  };

  return (
    <Card className="border-destructive/50 p-6">
      <div className="flex items-start gap-4">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-destructive">
            Delete Account
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Permanently delete your account and all associated data. This action
            cannot be undone. All reflections, audio recordings, chat history,
            insights, and subscription data will be permanently removed.
          </p>
          <Button
            variant="destructive"
            className="mt-4"
            onClick={() => setIsOpen(true)}
          >
            Delete My Account
          </Button>
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          onEscapeKeyDown={(e) => {
            if (deleteAccount.isPending) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (deleteAccount.isPending) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-destructive">
              Delete Your Account?
            </DialogTitle>
            <DialogDescription className="text-left">
              This will permanently delete:
            </DialogDescription>
          </DialogHeader>

          <ul className="list-disc pl-6 text-sm text-muted-foreground space-y-1">
            <li>All reflections and audio recordings</li>
            <li>AI feedback and insights</li>
            <li>Chat history</li>
            <li>Relationship tracker entries</li>
            <li>Your subscription (will be canceled immediately)</li>
            <li>Your login credentials</li>
          </ul>

          <p className="text-sm font-medium text-foreground">
            Type <span className="font-mono text-destructive">DELETE</span> to
            confirm:
          </p>

          <Input
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder='Type "DELETE" to confirm'
            disabled={deleteAccount.isPending}
            autoFocus
          />

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={deleteAccount.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!isConfirmed || deleteAccount.isPending}
            >
              {deleteAccount.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Permanently Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
